import type { FeatureLike } from 'ol/Feature';
import {
  Fill,
  Stroke,
  Style,
  Text,
} from 'ol/style';

import type { GeoJsonFeaturePropertiesDTO } from '@nora-monorepo/nora-api-client';
import { DrawToolType } from '@nora-monorepo/nora-api-client';

import { circleStyleFunction } from '#nora-ui/map/drawtools/styles/circle-style';
import { freehandStyleFunction } from '#nora-ui/map/drawtools/styles/freehand-style';
import { routeStyleFunction } from '#nora-ui/map/drawtools/styles/route-style';
import { rulerStyleFunction } from '#nora-ui/map/drawtools/styles/ruler-style';
import { spotStyleFunction } from '#nora-ui/map/drawtools/styles/spot-map-style';
import { symbolStyleFunction } from '#nora-ui/map/drawtools/styles/symbol-style';
import { tacticalAnnotationStyleFunction } from '#nora-ui/map/drawtools/styles/tactical-annotation-style';
import { createTextToolStyle } from '#nora-ui/map/drawtools/styles/text-tool-style';
import type { FeatureStyleProperties } from '#nora-ui/map/feature-properties';
import { getFeatureProperties } from '#nora-ui/map/feature-properties';
import {
  defaultFillColor,
  defaultFontStyle,
  defaultLineColor,
  defaultLineWidth,
} from '#nora-ui/map/map/DrawToolUIManager';
import { FeatureStyleManager } from '#nora-ui/map/map/FeatureStyleManager';

function createDefaultStyle(feature: FeatureLike): Style {
  const { fillColor, lineColor, lineWidth, labelText, font } = getFeatureProperties<FeatureStyleProperties>(feature);

  return new Style({
    fill: new Fill({
      color: fillColor ?? defaultFillColor,
    }),
    stroke: new Stroke({
      color: lineColor ?? defaultLineColor,
      width: lineWidth ?? defaultLineWidth,
    }),
    text: new Text({
      text: labelText,
      font: font ?? defaultFontStyle,
      fill: new Fill({
        color: fillColor ?? defaultFillColor,
      }),
    }),
  });
}

export function getFeatureStyle(feature: FeatureLike): Style | Style[] {
  const properties = getFeatureProperties<GeoJsonFeaturePropertiesDTO & FeatureStyleProperties>(feature);

  if (properties.hidden) {
    return new Style();
  }

  const drawToolType = properties.drawToolType ?? DrawToolType.unstyled;

  // NOTE: We should instead create a wrapper around Feature, which contain a 'render()' function or similar that contains both graphical icon/feature geometry, and label.
  switch (drawToolType) {
    case DrawToolType.tactical_annotation:
      return tacticalAnnotationStyleFunction(feature);
    case DrawToolType.circle:
      return FeatureStyleManager.addDynamicLabelToStylesFromFeatureData(feature, circleStyleFunction(feature));
    case DrawToolType.freehand:
      return FeatureStyleManager.addDynamicLabelToStylesFromFeatureData(feature, [freehandStyleFunction(feature)]);
    case DrawToolType.ruler:
      return rulerStyleFunction(feature);
    case DrawToolType.route:
      return routeStyleFunction();
    case DrawToolType.spot:
      return spotStyleFunction(feature);
    case DrawToolType.symbol: {
      return symbolStyleFunction(feature);
    }
    case DrawToolType.text:
      return createTextToolStyle(feature);
    case DrawToolType.rectangle:
    case DrawToolType.polygon:
    case DrawToolType.select:
    case DrawToolType.building_annotation:
    case DrawToolType.erase:
    case DrawToolType.line:
    case DrawToolType.move:
    case DrawToolType.unstyled:
      return FeatureStyleManager.addDynamicLabelToStylesFromFeatureData(feature, [createDefaultStyle(feature)]);
  }
}
